/**
 * Outreach Leads API.
 */
import api from '../../../api';
import type {
  Lead,
  LeadListResponse,
  LeadCreate,
  LeadUpdate,
  ColumnInspectionResponse,
  ImportPreviewResponse,
  ImportCommitResponse,
  ImportMetadata,
  BulkUpdateRequest,
  BulkUpdateResponse,
} from '../types';

const PREFIX = '/api/outreach/leads';

export const outreachLeadsApi = {
  list: async (params: {
    page?: number;
    page_size?: number;
    do_not_send?: boolean;
    is_suppressed?: boolean;
    tags?: string[];
    search?: string;
    country?: string[];
    city?: string[];
    therapy_interest?: string[];
    lead_source?: string[];
    lead_potential?: string[];
    added_by?: string[];
    role?: string[];
    domain?: string[];
    status?: string;
    last_contacted_days_ago?: number;
    min_emails_sent?: number;
    max_emails_sent?: number;
    exclude_lead_source?: string[];
    exclude_lead_potential?: string[];
    exclude_added_by?: string[];
    exclude_country?: string[];
    exclude_city?: string[];
    exclude_role?: string[];
    exclude_domain?: string[];
    exclude_tags?: string[];
    exclude_therapy_interest?: string[];
  }): Promise<LeadListResponse> => {
    const { data } = await api.get<LeadListResponse>(PREFIX, { params });
    return data;
  },

  create: async (lead: LeadCreate): Promise<Lead> => {
    const { data } = await api.post<Lead>(PREFIX, lead);
    return data;
  },

  update: async (leadId: string, update: LeadUpdate): Promise<Lead> => {
    const { data } = await api.patch<Lead>(`${PREFIX}/${leadId}`, update);
    return data;
  },

  inspectImportFile: async (file: File): Promise<ColumnInspectionResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await api.post<ColumnInspectionResponse>(
      `${PREFIX}/import/inspect`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return data;
  },

  previewImport: async (
    file: File,
    columnMapping: Record<string, string>
  ): Promise<ImportPreviewResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('column_mapping_json', JSON.stringify(columnMapping));
    const { data } = await api.post<ImportPreviewResponse>(
      `${PREFIX}/import/preview`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return data;
  },

  commitImport: async (
    file: File,
    columnMapping: Record<string, string>,
    importMetadata?: ImportMetadata
  ): Promise<ImportCommitResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('column_mapping_json', JSON.stringify(columnMapping));
    if (importMetadata) {
      formData.append('import_metadata_json', JSON.stringify(importMetadata));
    }
    const { data } = await api.post<ImportCommitResponse>(
      `${PREFIX}/import/commit`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return data;
  },

  /** Bulk update multiple leads at once */
  bulkUpdate: async (leadIds: string[], update: LeadUpdate): Promise<BulkUpdateResponse> => {
    const { data } = await api.patch<BulkUpdateResponse>(`${PREFIX}/bulk`, {
      lead_ids: leadIds,
      update,
    });
    return data;
  },

  /** Lightweight count-only endpoint (no row data returned). */
  count: async (params: {
    do_not_send?: boolean;
    is_suppressed?: boolean;
    tags?: string[];
    search?: string;
    country?: string[];
    city?: string[];
    therapy_interest?: string[];
    lead_source?: string[];
    lead_potential?: string[];
    added_by?: string[];
    role?: string[];
    domain?: string[];
    status?: string;
    last_contacted_days_ago?: number;
    min_emails_sent?: number;
    max_emails_sent?: number;
    exclude_lead_source?: string[];
    exclude_lead_potential?: string[];
    exclude_added_by?: string[];
    exclude_country?: string[];
    exclude_city?: string[];
    exclude_role?: string[];
    exclude_domain?: string[];
    exclude_tags?: string[];
    exclude_therapy_interest?: string[];
  }): Promise<{ total: number }> => {
    const { data } = await api.get<{ total: number }>(`${PREFIX}/count`, { params });
    return data;
  },

  /** Check which emails already exist in the database */
  checkEmails: async (emails: string[]): Promise<{ existing: string[] }> => {
    const { data } = await api.post<{ existing: string[] }>(
      `${PREFIX}/check-emails`,
      { emails }
    );
    return data;
  },

  /** Get cascading filter dropdown values scoped to current filter selections */
  getFilterOptions: async (params?: {
    do_not_send?: boolean;
    is_suppressed?: boolean;
    tags?: string[];
    search?: string;
    country?: string[];
    city?: string[];
    therapy_interest?: string[];
    lead_source?: string[];
    lead_potential?: string[];
    added_by?: string[];
    role?: string[];
    domain?: string[];
    status?: string;
    last_contacted_days_ago?: number;
    min_emails_sent?: number;
    max_emails_sent?: number;
    exclude_lead_source?: string[];
    exclude_lead_potential?: string[];
    exclude_added_by?: string[];
    exclude_country?: string[];
    exclude_city?: string[];
    exclude_role?: string[];
    exclude_domain?: string[];
    exclude_tags?: string[];
    exclude_therapy_interest?: string[];
  }): Promise<{
    roles: string[];
    domains: string[];
    countries: string[];
    cities: string[];
    tags: string[];
    therapy_interest: string[];
    added_by: string[];
    lead_source: string[];
    lead_potential: string[];
  }> => {
    const { data } = await api.get(`${PREFIX}/filter-options`, { params });
    return data;
  },
};
